import { menuRepository } from '@/service/repository/menuRepository'
import { arraysAreEqualIgnoringOrder, hideAllModals, hideOffcanvas, normalizeChoices, showModal } from '@/utils'
import { notificationRepository } from '@/service/repository/notificationRepository'
import { cartSessionRepository } from '@/service/repository/cartSessionRepository'
import { groupProductsBySkuAndOptions } from '@/utils/cartGrouping'
import { imageCacheService } from '@/service/imageCacheService'

// Debounced localStorage persistence to avoid blocking UI on rapid cart changes
let localStorageTimeout = null
const LOCALSTORAGE_DEBOUNCE_MS = 200

// Memoization cache for menusVenue getter to avoid recomputing on every access
let menusVenueCache = null
let menusVenueCacheKey = null

function persistCartToLocalStorage(cart) {
	if (localStorageTimeout) {
		clearTimeout(localStorageTimeout)
	}
	localStorageTimeout = setTimeout(() => {
		try {
			localStorage.setItem('cart', JSON.stringify(cart))
		} catch (e) {
			console.warn('Failed to persist cart to localStorage:', e)
		}
	}, LOCALSTORAGE_DEBOUNCE_MS)
}

/**
 * Deep freezes an object to prevent Vue from making it reactive.
 * This is used for read-only data like menus that never change during a session.
 * Frozen objects don't trigger Vue's reactivity system, improving performance.
 * @param {any} obj - The object to freeze
 * @returns {any} - The frozen object
 */
function deepFreeze(obj) {
	if (obj === null || typeof obj !== 'object' || Object.isFrozen(obj)) {
		return obj
	}
	
	// Freeze arrays
	if (Array.isArray(obj)) {
		obj.forEach(item => deepFreeze(item))
		return Object.freeze(obj)
	}
	
	// Freeze object properties
	Object.keys(obj).forEach(key => {
		const value = obj[key]
		if (value !== null && typeof value === 'object') {
			deepFreeze(value)
		}
	})
	
	return Object.freeze(obj)
}

/**
 * Creates a lightweight cart product by copying only essential fields.
 * This avoids Vue reactivity overhead on deeply nested productOptions.
 * @param {Object} product - The full product object from the menu
 * @returns {Object} - A lightweight copy suitable for the cart
 */
function createCartProduct(product) {
	// Note: We intentionally do NOT freeze productOptions here because
	// it breaks Vue reactivity when editing products in the cart.
	// The performance gain from freezing is outweighed by the editing issues.
	// The main performance gains come from selective field copying and menu freezing.
	
	return {
		// Essential identifiers
		id: product.id,
		sku: product.sku,
		name: product.name,
		shortName: product.shortName,
		
		// Pricing
		price: product.price,
		variablePrice: product.variablePrice,
		variablePriceMin: product.variablePriceMin,
		variablePriceMax: product.variablePriceMax,
		priceGuestHalfRation: product.priceGuestHalfRation,
		priceGuestGlass: product.priceGuestGlass,
		
		// Classification
		dishClass: product.dishClass,
		elaborationType: product.elaborationType,
		menuCategories: product.menuCategories,
		categoryNames: product.categoryNames,
		
		// Product type flags
		isMenu: product.isMenu,
		isUnitary: product.isUnitary,
		combo: product.combo,
		shareable: product.shareable,
		bulkSelectable: product.bulkSelectable,
		
		// Quantity settings
		unitaryIncrement: product.unitaryIncrement,
		servingQtyMin: product.servingQtyMin,
		servingQtyMax: product.servingQtyMax,
		
		// Options - keep as-is for cart editing compatibility
		productOptions: product.productOptions || [],
		priceModifiers: product.priceModifiers || [],
		
		// Display data
		description: product.description,
		jingle: product.jingle,
		pictureMenu: product.pictureMenu,
		allergens: product.allergens || [],
		
		// Process requirements
		productProcessRequirements: product.productProcessRequirements,
		
		// Quick access info
		quickAccess: product.quickAccess,
		
		// Menu category info (availability, price supplement, etc.)
		menuCategoryProductInfo: product.menuCategoryProductInfo,
		
		// Menu batch tracking (for menu products)
		menuBatchId: product.menuBatchId,
	}
}

// Función helper para extraer IDs de opciones considerando ambos formatos (recursiva)
function extractOptionIds(chosenOptions) {
	if (!chosenOptions) return []

	// Función recursiva para extraer todos los IDs de opciones anidadas
	const extractAllOptionIds = (options) => {
		if (!Array.isArray(options)) {
			return []
		}

		return options.flatMap(option => {
			const ids = []

			// Si es un string directo, agregarlo
			if (typeof option === 'string') {
				ids.push(option)
			}
			// Si es un objeto con id
			else if (typeof option === 'object' && option.id) {
				ids.push(option.id)

				// Si tiene options anidadas, procesarlas recursivamente
				if (Array.isArray(option.options)) {
					ids.push(...extractAllOptionIds(option.options))
				}
			}

			return ids
		})
	}

	return chosenOptions.flatMap((option) => {
		// Si es el formato con optionId y chosenIds
		if (option.optionId && option.chosenIds) {
			return extractAllOptionIds(option.chosenIds)
		}
		// Si es el formato directo con id y options
		else if (typeof option === 'object' && option.id && Array.isArray(option.options)) {
			return extractAllOptionIds(option.options)
		}
		return []
	})
}

// Construye el mapa plano sku -> [ids] a partir de drinksIncludedInMenuByBatch, que es el que le tenemos que pasar al endpoint
function rebuildDrinksIncludedInMenuFromBatches(state) {
	const byBatch = state.drinksIncludedInMenuByBatch || {}
	const aggregated = {}

	Object.values(byBatch).forEach((batchMap) => {
		Object.entries(batchMap || {}).forEach(([sku, ids]) => {
			if (!Array.isArray(ids)) return
			if (!aggregated[sku]) aggregated[sku] = new Set()
			ids.forEach((id) => {
				if (id != null) aggregated[sku].add(id)
			})
		})
	})

	state.drinksIncludedInMenu = Object.fromEntries(
		Object.entries(aggregated).map(([sku, set]) => [sku, Array.from(set)])
	)
}

const Status = {
	CREATED: 0,
	ACCEPTED: 1,
	PREPARING: 2,
	READY: 3,
	CONSUMING: 4,
	CONSUMED: 5,
	RETIRED: 6,
	IN_HOT_TABLE: 7,
	CANCELLED: -1,
	ERROR: -2,
	LOSS: -3,
	REJECTED: -4,
	SERVED: 100
}

export const StatusLabels = {
	[Status.CREATED]: 'Creado',
	[Status.ACCEPTED]: 'Aceptado',
	[Status.PREPARING]: 'Preparando',
	[Status.READY]: 'Esperando a llevar',
	[Status.CONSUMING]: 'Consumiendo',
	[Status.CONSUMED]: 'Consumido',
	[Status.RETIRED]: 'Retirado',
	[Status.IN_HOT_TABLE]: 'En mesa caliente',
	[Status.CANCELLED]: 'Cancelado',
	[Status.ERROR]: 'Error',
	[Status.LOSS]: 'Mermado',
	[Status.REJECTED]: 'Rechazado',
	[Status.SERVED]: 'Servido'
}

const state = {
	Status,

	menuProducts: [],
	menus: [],
	productsCart: [],
	optionTypePriceModifiers: [],
	dishClasses: [],
	isAccountRequester: false,
	isOpenModalSearch: false,
	getProduct: null,

	categorySelected: null,

	cartClientMessage: null,
	isValidReservation: false,

	isProcessingCart: false,

	productId: null,

	safeProductToCart: {},
	//Estado donde vamos a guardar las bebidas que queramos incluir en el menu
	drinksIncludedInMenu: {},
	//Estado donde guardamos las bebidas que ya están en algun menú
	drinksAlreadyMenuIncludedIds: [],

	drinksIncludedInMenuByBatch: {},
}

const getters = {
	status: (status) => status.Status,
	statusLabels: () => StatusLabels,

	isValidReservation: (state) => {
		return state.isValidReservation
	},
	menus: (state) => {
		return state.menus
	},

	drinksIncludedInMenu: (state) =>{
		return state.drinksIncludedInMenu
	},

	drinksAlreadyMenuIncludedIds: (state) =>{
		return state.drinksAlreadyMenuIncludedIds
	},

	getDrinksIncludedInMenu: (state) => (sku) => {
		if (!sku) return []
		return state.drinksIncludedInMenu[sku] || []
	},

	getDrinksIncludedInMenuForBatch: (state) => (menuBatchId, sku) => {
		if (!menuBatchId) return []
		const batchMap = state.drinksIncludedInMenuByBatch[menuBatchId]
		if (!batchMap) return []
		return batchMap[sku] || []
	},

	menusVenue: (state) => {
		// Memoization: return cached result if menus haven't changed
		// This avoids expensive nested transformations on every access
		const cacheKey = state.menus.length + '_' + (state.menus[0]?.id || '')
		if (menusVenueCacheKey === cacheKey && menusVenueCache !== null) {
			return menusVenueCache
		}

		const result = state.menus.map((menuItem) => {
			// Convertir menuCategories a un array si es un objeto
			const categories = Array.isArray(menuItem.menuCategories) ? menuItem.menuCategories : [menuItem.menuCategories] // Convertir a array si es un objeto

			return {
				...menuItem,
				menuCategories: categories.map((category) => {
					// Convertir products a un array si es un objeto
					const products = Array.isArray(category.products) ? category.products : category.products ? Object.values(category.products) : [] // Convertir a array si es un objeto

					return {
						...category,
						products: products.map((product) => {
							// Convertir productOptions a un array si es un objeto
							const productOptionsArray = Array.isArray(product.productOptions) ? product.productOptions : product.productOptions ? Object.values(product.productOptions) : [] // Convertir a array si es un objeto

							return {
								...product,
								productOptions: productOptionsArray.map((productOption) => {
									// Si options es un array, filtrar las opciones
									const filteredOptions = Array.isArray(productOption.options)
										? productOption.options.filter((option) => option.enabled >= 1) // Filtrar opciones
										: [] // Si options no es un array, devolvemos un array vacío

									return {
										...productOption,
										options: filteredOptions
									}
								})
							}
						})
					}
				})
			}
		})

		// Cache the result
		menusVenueCache = result
		menusVenueCacheKey = cacheKey
		return result
	},
	menuProducts: (state) => {
		return state.menus.flatMap((menu) => {
			// Convertir menuCategories a un array si es un objeto
			const categories = Array.isArray(menu.menuCategories) ? menu.menuCategories : [menu.menuCategories] // Envuelve el objeto en un array

			return categories.flatMap((category) => {
				// Convertir products a un array si es un objeto
				const products = Array.isArray(category.products) ? category.products : category.products ? Object.values(category.products) : [] // Convierte el objeto a array si no es nulo

				// Filtrar los productos cuya propiedad isMenu sea "SI"
				return products.filter((product) => product.isMenu === 'SI')
			})
		})
	},
	productsCart: (state) => {
		return state.productsCart
	},
	
	/**
	 * Optimized getter that returns a Map of SKU -> { cartItemCount, products }
	 * This is computed once and cached, avoiding O(n) lookups in every ProductCard
	 */
	productCountsBySku: (state) => {
		const countMap = {}
		for (const product of state.productsCart) {
			if (!countMap[product.sku]) {
				countMap[product.sku] = { cartItemCount: 0, products: [] }
			}
			countMap[product.sku].cartItemCount += product.cartItemCount
			countMap[product.sku].products.push(product)
		}
		return countMap
	},
	
	/**
	 * Get product count for a specific SKU - O(1) lookup
	 */
	getProductCountBySku: (state, getters) => (sku) => {
		return getters.productCountsBySku[sku] || { cartItemCount: 0, products: [] }
	},
	dishClasses: (state) => {
		return state.dishClasses
	},
	optionTypePriceModifiers: (state) => {
		return state.optionTypePriceModifiers
	},
	isAccountRequester: (state) => {
		return state.isAccountRequester
	},
	isOpenModalSearch: (state) => {
		return state.isOpenModalSearch
	},
	getProduct: (state) => {
		return state.getProduct
	},

	categorySelected: (state) => {
		return state.categorySelected
	},

	selectedCategory: (state) => {
		if (!state.categorySelected) {
			return []
		}

		for (const menu of state.menus) {
			const foundCategory = menu.menuCategories.find((cat) => {
				return cat.id === state.categorySelected.id && cat.name === state.categorySelected.name
			})

			if (foundCategory) {
				return foundCategory
			}
		}

		return []
	},

	cartClientMessage: (state) => {
		return state.cartClientMessage
	},

	allMenuDrinks: (state) => {
		const menus = state.menus || []
		// 1) filtrar menús de bebidas
		const drinksMenus = menus.filter((menu) => menu.menuTypeName?.toLowerCase() === 'bebida')

		const products = []

		// 2) recursión CORRECTA: cada vez trabajamos con "category"
		const traverse = (categories) => {
			if (!Array.isArray(categories)) return

			categories.forEach((category) => {
				// a) productos de ESTA categoría
				if (Array.isArray(category.products)) {
					products.push(...category.products)
				}
				// b) recursión sobre las subcategorías de ESTA category
				traverse(category.products)
				traverse(category.children)
			})
		}

		// 3) arrancamos desde menu.categories de cada drinksMenu
		drinksMenus.forEach((menu) => traverse(menu.menuCategories))
		return products
	},

	filteredMenus(state) {
		// Resto de la lógica original de filtrado (basada en menus y isValidReservation)
		const ensureArray = (arr) => (Array.isArray(arr) ? arr : [])
		const compareBySortOrder = (a, b) => {
			const orderA = a.sortOrderCustomer ?? a.sortOrder ?? 0
			const orderB = b.sortOrderCustomer ?? b.sortOrder ?? 0
			return orderA - orderB
		}

		const filterProducts = (products) => ensureArray(products)
		/*.filter((product) => (this.canPlaceOrders ? product.enabled === 1 : true))*/

		const filterCategoriesRecursively = (category) => {
			if (!category || typeof category !== 'object') return null

			/*const shouldFilter = this.canPlaceOrders ? category.enabled !== 1 || category.enabledCustomer === 0 : false
			if (shouldFilter) return null*/

			const filteredChildren = ensureArray(category.children).map(filterCategoriesRecursively).filter(Boolean).sort(compareBySortOrder)
			const filteredMenuCategories = ensureArray(category.menuCategories).map(filterCategoriesRecursively).filter(Boolean).sort(compareBySortOrder)
			const filteredProducts = filterProducts(category.products)

			if (!filteredChildren.length && !filteredMenuCategories.length && !filteredProducts.length) return null

			return {
				...category,
				children: filteredChildren,
				menuCategories: filteredMenuCategories,
				products: filteredProducts
			}
		}

		return ensureArray(state.menus)
			/*.filter((menu) => (this.canPlaceOrders ? menu.enabled === 1 : true))*/
			.map((menu) => ({
				...menu,
				menuCategories: ensureArray(menu.menuCategories).map(filterCategoriesRecursively).filter(Boolean).sort(compareBySortOrder)
			}))
			.filter((menu) => menu.menuCategories.length > 0)
			.sort(compareBySortOrder)
	},

	recommendedProducts(state) {
		const topChoiceProducts = []

		// Función recursiva para buscar productos con topChoice en una categoría
		const findTopChoiceProducts = (category) => {
			// Buscar productos que cumplan la condición en la categoría actual
			if (Array.isArray(category.products)) {
				category.products.forEach((product) => {
					if (product.topChoice === 'SI') {
						topChoiceProducts.push(product)
					}
				})
			}

			// Procesar recursivamente cada subcategoría
			if (Array.isArray(category.menuCategories)) {
				category.menuCategories.forEach((subCategory) => {
					findTopChoiceProducts(subCategory)
				})
			}

			// Procesar recursivamente cada categoría de children
			if (Array.isArray(category.children)) {
				category.children.forEach((childCategory) => {
					findTopChoiceProducts(childCategory)
				})
			}
		}

		// Recorrer cada menú y buscar productos en todas sus categorías
		state.menus.forEach((menu) => {
			if (Array.isArray(menu.menuCategories)) {
				menu.menuCategories.forEach((category) => {
					findTopChoiceProducts(category)
				})
			}
		})

		return topChoiceProducts
	},

	// Getter para encontrar un producto por productId considerando la recursividad
	findProductById: (state) => (productId) => {
		let foundProduct = null

		// Función recursiva para buscar un producto por ID en una categoría
		const findProductRecursively = (category) => {
			// Si ya encontramos el producto, no continuar buscando
			if (foundProduct) return

			// Buscar productos en la categoría actual
			if (Array.isArray(category.products)) {
				const product = category.products.find((product) => product.id === productId || product.sku === productId)
				if (product) {
					foundProduct = product
					return
				}
			}

			// Procesar recursivamente cada subcategoría (menuCategories)
			if (Array.isArray(category.menuCategories)) {
				category.menuCategories.forEach((subCategory) => {
					findProductRecursively(subCategory)
				})
			}

			// Procesar recursivamente cada categoría de children
			if (Array.isArray(category.children)) {
				category.children.forEach((childCategory) => {
					findProductRecursively(childCategory)
				})
			}
		}

		// Recorrer cada menú y buscar el producto en todas sus categorías
		for (const menu of state.menus) {
			if (Array.isArray(menu.menuCategories)) {
				for (const category of menu.menuCategories) {
					findProductRecursively(category)
					// Si encontramos el producto, salir del bucle
					if (foundProduct) break
				}
			}
			// Si encontramos el producto, salir del bucle
			if (foundProduct) break
		}

		return foundProduct
	},

	/**
	 * Busca un producto por SKU: primero en state.menuProducts (lista plana de la cuenta),
	 * si no está, en state.menus (árbol de categorías). Para usar al agregar desde histórico.
	 */
	findProductBySku: (state, getters) => (sku) => {
		if (!sku) return null
		const fromMenuProducts = Array.isArray(state.menuProducts) && state.menuProducts.length > 0
			? state.menuProducts.find((p) => p.sku === sku)
			: null
		if (fromMenuProducts) return fromMenuProducts
		return getters.findProductById(sku)
	},

	safeProductToCart: (state) => {
		return state.safeProductToCart
	},
	findProductAddComment: (state) => (productOrSku) => {
		const sku = typeof productOrSku === 'string'
			? productOrSku
			: productOrSku?.sku

		if (!sku) return []

		return state.productsCart.filter((item) => item.sku === sku)
	},
	findProductAddCommentWithOptions: (state) => (product) => {
		// Si no hay producto o SKU, retornar array vacío
		if (!product || !product.sku) return []

		// Las opciones del producto ya están normalizadas
		const normalizedChosenOptions = product.chosenOptions

		// Filtrar productos del carrito que tengan el mismo SKU y las mismas opciones elegidas
		return state.productsCart.filter((item) => {
			// Verificar que tengan el mismo SKU
			if (item.sku !== product.sku) return false

			// Verificar que tengan las mismas opciones elegidas
			const itemNormalizedOptions = normalizeChoices(item.chosenOptions)
			return arraysAreEqualIgnoringOrder(itemNormalizedOptions, normalizedChosenOptions)
		})
	},
	findProductInCart: (state) => (product) => {
		const normalizedChosenOptions = normalizeChoices(product.chosenOptions)
		return state.productsCart.find(
			(productCart) =>
				productCart.sku === product.sku &&
				arraysAreEqualIgnoringOrder(normalizeChoices(productCart.chosenOptions), normalizedChosenOptions)
		)
	},
}

const mutations = {
	setProductId(state, productId) {
		state.productId = productId
	},
	setMenuProducts(state, payload) {
		state.menuProducts = payload.menuProducts
	},
	setMenus(state, payload) {
		// Deep freeze menus to prevent Vue reactivity overhead on read-only data
		// Menu data is loaded once and never mutated during a session
		state.menus = payload.menus.map(menu => deepFreeze(menu))
		// Invalidate menusVenue cache when menus change
		menusVenueCache = null
		menusVenueCacheKey = null
	},
	setIsValidReservation(state, payload) {
		state.isValidReservation = payload
	},
	setDishClasses(state, payload) {
		state.dishClasses = payload.dishClasses
	},
	setOptionTypePriceModifiers(state, payload) {
		state.optionTypePriceModifiers = payload.optionTypePriceModifiers
	},
	setProductsCart(state, payload) {
		state.productsCart = payload.productsCart
		// Persist cart to localStorage (debounced)
		persistCartToLocalStorage(state.productsCart)
	},
	setProduct(state, payload) {
		state.getProduct = payload
	},
	addProductToCart(state, payload) {
		let units = payload.units
		const normalizedOptions = normalizeChoices(payload.options)

		// Baliza que ponemos ficticia a los menús para poder identificarlos y separarlos.
		const batchId = payload.product.menuBatchId ?? null

		// Note: isMenuIncluded is set in the newProduct object, not on the original (frozen) product
		if (payload.product.isUnitary === 'SI') {
			units = payload.product.servingQtyMin
		}

		//  Comprobamos si ya existe un producto igual:  mismo sku + mismo batchId + mismas opciones
		const isIncluded = state.productsCart.some((productCart) => {
			if (productCart.sku !== payload.product.sku) return false

			const cartBatchId = productCart.menuBatchId ?? null
			if (cartBatchId !== batchId) return false

			return arraysAreEqualIgnoringOrder(
				productCart.chosenOptions,
				normalizedOptions
			)
		})

		if (!isIncluded) {
			const cartItemCount = units || 1
			// Use lightweight cart product to avoid reactivity overhead on nested productOptions
			const baseProduct = createCartProduct(payload.product)
			const newProduct = {
				...baseProduct,
				chosenOptions: normalizedOptions,
				cartItemCount,
				isMenuIncluded: false,
				// solo añadimos menuBatchId si viene definido
				...(batchId ? { menuBatchId: batchId } : {}),
				timestamp: Date.now(),
				comments: Array.isArray(payload.product.comments)
					? payload.product.comments
					: []
			}

			state.productsCart = [
				...state.productsCart,
				newProduct
			]
		} else {
			// Buscamos el producto ya existente con misma sku + opciones + batch
			const existingProduct = state.productsCart.find((productCart) => {
				if (productCart.sku !== payload.product.sku) return false
				const cartBatchId = productCart.menuBatchId ?? null
				if (cartBatchId !== batchId) return false

				return arraysAreEqualIgnoringOrder(
						productCart.chosenOptions,
						normalizedOptions
					)
				})

				units = payload.product.unitaryIncrement
				existingProduct.cartItemCount += units || 1
				existingProduct.timestamp = Date.now()
			}
		
		// Persist cart to localStorage (debounced)
		persistCartToLocalStorage(state.productsCart)
	},
	updateProductInCart(state, { index, product, options }) {
		if (index < 0 || index >= state.productsCart.length) return

		const original = state.productsCart[index]
		if (!original) return

		// Normalizar opciones igual que en addProductToCart
		const normalizedOptions = normalizeChoices(options)

		// Mantener unidades actuales
		let units = original.cartItemCount || 1

		// Use lightweight cart product to avoid reactivity overhead
		const updatedBase = createCartProduct(product)

		// Mantener coherencia con addProductToCart
		updatedBase.isMenuIncluded = false

		// Si es unitario, respetamos las unidades ya existentes
		if (updatedBase.isUnitary === 'SI') {
			units = original.cartItemCount || updatedBase.servingQtyMin || 1
		}

		const newProduct = {
			...updatedBase,
			chosenOptions: normalizedOptions,
			cartItemCount: units,
			timestamp: Date.now(),
			comments: Array.isArray(product.comments)
				? product.comments
				: []
		}

		//  Comprobar si al editar hemos acabado con la misma combinación SKU+opciones  que otra línea del carrito → fusionamos líneas
		const duplicateIndex = state.productsCart.findIndex((productCart, idx) =>
			idx !== index &&
			productCart.sku === newProduct.sku &&
			arraysAreEqualIgnoringOrder(productCart.chosenOptions, normalizedOptions)
		)

		if (duplicateIndex !== -1) {
			// Fusionar cantidades en la línea duplicada
			const target = state.productsCart[duplicateIndex]
			target.cartItemCount += newProduct.cartItemCount
			target.timestamp = Date.now()

			// Eliminar la línea original editada
			state.productsCart.splice(index, 1)
		} else {
			// Sustituir la línea editada por el nuevo producto
			state.productsCart.splice(index, 1, newProduct)
		}

		// Use debounced persistence to avoid blocking UI
		persistCartToLocalStorage(state.productsCart)
	},


	addUnit(state, payload) {
		const normalizedChosenOptions = normalizeChoices(payload.product.chosenOptions)

		const productCart = state.productsCart.find((productCart) => productCart.sku === payload.product.sku && arraysAreEqualIgnoringOrder(normalizeChoices(productCart.chosenOptions), normalizedChosenOptions))

		if (!productCart) {
			return
		}

		if (productCart.isUnitary === 'SI') {
			productCart.cartItemCount += payload.product.unitaryIncrement || 1
		} else {
			productCart.cartItemCount++
		}
		
		// Persist cart to localStorage (debounced)
		persistCartToLocalStorage(state.productsCart)
	},
	setSafeProductToCart(state, payload) {
		state.safeProductToCart = payload
	},
	addCommentToProductInCart(state, { product, comment, count }) {
		const normalizedChosenOptions = normalizeChoices(product.chosenOptions)

		const productCart = state.productsCart.find(
			(productCart) =>
				productCart.sku === product.sku &&
				arraysAreEqualIgnoringOrder(
					normalizeChoices(productCart.chosenOptions),
					normalizedChosenOptions
				)
		)

		if (productCart) {
			if (!productCart.comments) {
				productCart.comments = []
			}

			const commentObj = {
				count: count,
				comment: comment
			}

			productCart.comments.push(commentObj)
		}
	},
	removeCommentFromProductInCart(state, { product, comment }) {
		const normalizedChosenOptions = normalizeChoices(product.chosenOptions)

		const productCart = state.productsCart.find(
			(productCart) =>
				productCart.sku === product.sku &&
				arraysAreEqualIgnoringOrder(
					normalizeChoices(productCart.chosenOptions),
					normalizedChosenOptions
				)
		)

		if (productCart && productCart.comments) {
			const index = productCart.comments.indexOf(comment)
			if (index !== -1) {
				productCart.comments.splice(index, 1)
			}
		}
	},
	modifyDishClassOfCartProduct(state, payload) {
		const productChosenOptionsIds = extractOptionIds(payload.product.chosenOptions)
		const selectedDishClassId = payload.selectedDishClassId

		const dishClass = state.dishClasses.find(({ id }) => id === selectedDishClassId)
		state.productsCart = state.productsCart.map((product) => {
			const productCartChosenOptions = extractOptionIds(product.chosenOptions)

			// Solo procesar productos con el mismo SKU
			if (product.sku === payload.product.sku) {
				const optionsMatch = arraysAreEqualIgnoringOrder(productChosenOptionsIds, productCartChosenOptions)
				const dishClassMismatch = product.dishClass.id !== selectedDishClassId

				// Si es el producto exacto (mismo SKU + mismas opciones)
				if (optionsMatch) {
					if (dishClassMismatch) {
						// Aplicar el cambio de clase de plato
						return { ...product, modifyDishClass: dishClass }
					} else {
						// Si ya tiene la misma clase de plato, remover modifyDishClass
						return Object.fromEntries(Object.entries(product).filter(([key]) => key !== 'modifyDishClass'))
					}
				}
				// Si no es el producto exacto, no hacer nada (mantener su estado actual)
			}

			return { ...product }
		})
	},
	updateShareableProduct(state, payload) {
		const productChosenOptionsIds = extractOptionIds(payload.product.chosenOptions)
		state.productsCart = state.productsCart.map((product) => {
			const productCartChosenOptions = extractOptionIds(product.chosenOptions)

			if (product.sku === payload.product.sku) {
				const optionsMatch = arraysAreEqualIgnoringOrder(productChosenOptionsIds, productCartChosenOptions)
				if (optionsMatch) {
					return { ...product, isShareable: payload.shareable }
				}
			}

			return { ...product }
		})
	},

	subtractUnit(state, payload) {
		const normalizedChosenOptions = normalizeChoices(payload.product.chosenOptions)

		const productCart = state.productsCart.find((productCart) => {
			const normalizedProductCartChosenOptions = normalizeChoices(productCart.chosenOptions)
			const isMatching = productCart.sku === payload.product.sku && arraysAreEqualIgnoringOrder(normalizedProductCartChosenOptions, normalizedChosenOptions)
			return isMatching
		})

		if (!productCart) {
			return
		}

		const valueDecrement = payload.product.unitaryIncrement || 1
		const element = document.getElementById(payload.product.sku)
		if (valueDecrement === 0) {
			element.classList.remove('addProduct')
		}

		if (productCart.cartItemCount === 1 || productCart.cartItemCount === productCart.servingQtyMin) {
			const newProductsCart = state.productsCart.filter((productCart) => productCart.sku !== payload.product.sku || !arraysAreEqualIgnoringOrder(normalizeChoices(productCart.chosenOptions), normalizedChosenOptions))
			state.productsCart = newProductsCart
		} else {
			productCart.cartItemCount -= valueDecrement
		}
		
		// Persist cart to localStorage (debounced)
		persistCartToLocalStorage(state.productsCart)
	},
	deleteProductFromCart(state, payload) {
		const { sku, menuBatchId } = payload

		state.productsCart = state.productsCart.filter((product) => {
			// productos de otro sku ,se quedan siempre
			if (product.sku !== sku) return true
			// si no se pasa menuBatchId en el payload, borramos todos los productos con ese sku
			if (menuBatchId === undefined) {
				return false
			}
			// Si si pasamos el batchId, borramos aquel o aquellos productos que coincidan con ese batchId
			const productBatchId = product.menuBatchId ?? null
			const targetBatchId = menuBatchId ?? null
			// true = se queda, false = se borra
			return productBatchId !== targetBatchId
		})
		
		// Persist cart to localStorage (debounced)
		persistCartToLocalStorage(state.productsCart)
	},

	deleteBatchDrinks(state, menuBatchId) {
		if (!menuBatchId) return
		delete state.drinksIncludedInMenuByBatch[menuBatchId]
		rebuildDrinksIncludedInMenuFromBatches(state)
	},

	setIsAccountRequester(state, value) {
		state.isAccountRequester = value
	},
	setIsOpenModalSearch(state, value) {
		state.isOpenModalSearch = value
	},
	updateMenuProduct(state, payload) {
		const { product: updatedProduct } = payload

		// Create new menu structure without mutating frozen objects
		const newMenus = state.menus.map((menu) => ({
			...menu,
			menuCategories: menu.menuCategories?.map((category) => ({
				...category,
				products: category.products?.map((product) => {
					if (product.sku === updatedProduct.sku) {
						return updatedProduct
					}

					if (product.isMenu === 'SI') {
						const productOptions = updatedProduct.productOptions
						// Return a new product object with updated productOptions (don't mutate frozen product)
						return {
							...product,
							productOptions: product.productOptions.map((existingOption) => {
								const matchingOption = productOptions?.find((newOption) => newOption.id === existingOption.id)
								return matchingOption || existingOption
							})
						}
					}

					return product
				})
			}))
		}))
		
		// Freeze and assign the new menus
		state.menus = newMenus.map(menu => deepFreeze(menu))
		// Invalidate cache
		menusVenueCache = null
		menusVenueCacheKey = null
	},

	setCategorySelected(state, payload) {
		state.categorySelected = payload
	},

	setCartClientMessage(state, payload) {
		state.cartClientMessage = payload
	},

	SET_PROCESSING(state, flag) {
		state.isProcessingCart = flag
	},

	setDrinksIncludedInMenu(state, payload) {
		state.drinksIncludedInMenu = payload && typeof payload === 'object'
			? { ...payload }
			: {}
	},

	setDrinksIncludedInMenuForBatch(state, { menuBatchId, sku, productOrderIds }) {
		if (!menuBatchId || !sku) return

		const prevBatchMap = state.drinksIncludedInMenuByBatch[menuBatchId] || {}

		state.drinksIncludedInMenuByBatch = {
			...(state.drinksIncludedInMenuByBatch || {}),
			[menuBatchId]: {
				...prevBatchMap,
				[sku]: Array.isArray(productOrderIds) ? [...productOrderIds] : []
			}
		}

		rebuildDrinksIncludedInMenuFromBatches(state)
	},
	// Limpia todas las bebidas marcadas como incluidas en menú en esta sesión
	clearDrinksIncludedInMenu(state) {
		state.drinksIncludedInMenu = {}
		state.drinksIncludedInMenuByBatch = {}
	},

	appendDrinksAlreadyMenuIncluded(state, productOrderIds) {
		if (!Array.isArray(productOrderIds)) return

		const current = new Set(state.drinksAlreadyMenuIncludedIds || [])
		productOrderIds.forEach(id => {
			if (id) current.add(id)
		})

		state.drinksAlreadyMenuIncludedIds = Array.from(current)
	}
}

const actions = {
	async loadMenus({ commit, rootGetters }) {
		try {
			const menusProductResult = await menuRepository.getAvailableMenus()
			if (menusProductResult.result === 0) {
				commit('setMenus', { menus: menusProductResult.menus })
				commit('setDishClasses', { dishClasses: menusProductResult.dishClasses })
				commit('setOptionTypePriceModifiers', { optionTypePriceModifiers: menusProductResult.optionTypePriceModifiers })
				
				// Preload product and allergen images in the background for faster commander performance
				const urlVenue = rootGetters['venue/urlVenue']
				const resourceId = rootGetters['venue/resourceId']
				if (urlVenue) {
					imageCacheService.preloadProductImages(menusProductResult.menus, urlVenue, resourceId)
				}
				// Preload allergen images from actual menu data
				imageCacheService.preloadAllergensFromMenus(menusProductResult.menus)
			}
		} catch (error) {
			console.error('Failed to fetch venue menus', error)
		}
	},
	async loadMenuProducts({ commit }) {
		try {
			const menuProductResult = await menuRepository.getAvailableMenuProducts()
			if (menuProductResult.result === 0) {
				commit('setMenuProducts', { menuProducts: menuProductResult.menuProducts })
			}
		} catch (error) {
			console.error('Failed to fetch venue menus', error)
		}
	},
	async updateMenuProduct(payload) {
		try {
			const updateMenuProductResult = await menuRepository.updateMenuProduct(payload)
			if (updateMenuProductResult.result === 0) {
				return
			}
		} catch (error) {
			return null
		}
	},

	async requestAccount({ commit }, payload) {
		const reservationInfo = payload.reservationInfo
		const user = payload.user
		const paymentMethod = payload.paymentMethod
		let targetName = 'Comandero'
		let uid = null

		if (user?.name !== undefined) {
			targetName = user.name
		}
		if (user?.hostCode !== undefined) {
			uid = user.hostCode
		}

		const notification = {
			status: 0,
			type: 'ticket_request',
			subtype: 0,
			target: targetName,
			paymentMethod: paymentMethod
		}

		const data = {
			pax: reservationInfo.pax,
			reservationId: reservationInfo.id,
			reservationName: targetName,
			uid: uid
		}

		const resultTicket = await notificationRepository.requestTicket({
			notification: notification,
			data: data
		})

		if (resultTicket.result === 0) {
			commit('setProductsCart', { productsCart: [] })
		}

		return resultTicket
	},
	changeAccountRequesterStatus({ commit }, status) {
		commit('setIsAccountRequester', status)
	},
	changeIsOpenModalSearch({ commit }, status) {
		commit('setIsOpenModalSearch', status)
	},

	getProduct({ commit }, payload) {
		commit('setProduct', payload)
	},

	setCategorySelected({ commit }, category) {
		commit('setCategorySelected', category)
	},

	async confirmCart({ state, commit, rootGetters, dispatch }, { reservation }) {
		commit('SET_PROCESSING', true)
		const user = rootGetters['user/user']

		const orderInfo = {
			name: user.name,
			welKey: user.welKey,
			userId: user.id,
			pax: reservation.pax,
			source: 2,
			clientMessage: state.cartClientMessage,
			orderReservationName: user.name,
			reservationId: reservation.id,
			av: 0
		}

		// Group products by SKU + options for new format
		const products = groupProductsBySkuAndOptions(state.productsCart)

		const resultOrder = await cartSessionRepository.createCartSessionOrder(orderInfo, products)

		if (resultOrder.result === 0) {
			// TODO: saber que hace exactamente
			// this.messageCart = { clientName: this.user.name, products: this.productsCart };
			showModal('modal-message-products')
			commit('setProductsCart', { productsCart: [] })
			commit('setCartClientMessage', null)

			commit('SET_PROCESSING', false)
		}

		if (resultOrder.result === -2) {
			commit('setProductsCart', { productsCart: [] })
			await dispatch('copilot/showToastError', { message: `La reserva ha finalizado` })
			commit('SET_PROCESSING', false)
		}
		commit('SET_PROCESSING', false)

		hideAllModals()
		hideOffcanvas()
	}
}

export default {
	namespaced: true,
	state,
	mutations,
	getters,
	actions
}
